// Staff tasks — shell-scoped paths. Each shell only links to its own routes
// (D-7): a task linked to a record another shell owns shows its label only.
import { formatPlainDate, parsePlainDate } from '../../lib/datetime'
import type { TaskLink, TaskLinkKind, TaskPerson } from './api'

export type TaskShell = 'admin' | 'seller' | 'supply'

/**
 * What the Tasks page shows, kept in `?view=`: a task list, the projects
 * (all, one folder, or those without one), one person's tasks, or the rules.
 */
export type TaskView =
  | 'mine'
  | 'all'
  | 'created'
  | 'projects'
  | 'automations'
  | `folder:${string}`
  | `person:${string}`

export function parseView(
  search: { view?: unknown; tab?: unknown },
  fallback: TaskView = 'all',
): TaskView {
  // `tab` is the older name (part 3/4 links) for the same thing.
  const raw = typeof search.view === 'string' ? search.view : search.tab
  if (raw === 'mine' || raw === 'all' || raw === 'created' || raw === 'projects' || raw === 'automations')
    return raw
  if (typeof raw === 'string' && /^(folder|person):[\w-]+$/.test(raw)) return raw as TaskView
  return fallback
}

export const tasksBasePath = (shell: TaskShell) => `/${shell}/workspace/tasks` as const

type RecordRoute = { to: string; params: Record<string, string> }

export const taskDetailRoute = (shell: TaskShell, taskId: string): RecordRoute => ({
  to: `/${shell}/workspace/tasks/$taskId`,
  params: { taskId },
})

export const projectRoute = (shell: TaskShell, projectId: string): RecordRoute => ({
  to: `/${shell}/workspace/tasks/projects/$projectId`,
  params: { projectId },
})

const RECORD_ROUTES: Record<TaskShell, Partial<Record<TaskLinkKind, (id: string) => RecordRoute>>> = {
  admin: {
    order: (id) => ({ to: '/admin/orders/$id', params: { id } }),
    product: (id) => ({ to: '/admin/catalog/products/$id', params: { id } }),
    sample: (id) => ({ to: '/admin/samples/$id', params: { id } }),
  },
  seller: {
    order: (id) => ({ to: '/seller/my-orders/$id', params: { id } }),
    product: (id) => ({ to: '/seller/catalog/$productId', params: { productId: id } }),
  },
  supply: {
    sample: (id) => ({ to: '/supply/samples/$id', params: { id } }),
    work_order: (id) => ({ to: '/supply/work-orders/$id', params: { id } }),
  },
}

export function recordRoute(shell: TaskShell, link: TaskLink): RecordRoute | null {
  const build = RECORD_ROUTES[shell][link.kind]
  return build ? build(link.id) : null
}

/** "Ayşe, Mehmet +2" — compact list of assignee names for table rows. */
export function assigneeNames(people: TaskPerson[], more: (n: number) => string, max = 2): string {
  const shown = people.slice(0, max).map((person) => person.display_name)
  return people.length > max ? `${shown.join(', ')} ${more(people.length - max)}` : shown.join(', ')
}

/** An ISO calendar day ("2026-11-05") formatted for display, or null. */
export function formatDay(value: string | null | undefined): string | null {
  const day = value ? parsePlainDate(value) : null
  return day ? formatPlainDate(day) : null
}
